import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios'
import { stripSensitiveLogData } from '@/utils/security'
import {
  buildCalculatorCacheKey,
  CalculatorResponseCache,
  shouldCacheCalculatorRequest,
} from '@/utils/calculatorResponseCache'
import { createCalculatorDebounce } from '@/utils/calculatorDebounce'
import { getCsrfTokenFallback, setCsrfTokenFallback } from '@/utils/csrfToken'
import { clearPersistedAuthSession, dispatchAuthSessionInvalidated } from '@/utils/authSession'

// Base API configuration - use environment variable in production
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8001'

export type ApiErrorCode = 'forbidden' | 'not_found' | 'network' | 'server_error'

export class ApiError extends Error {
  status: number
  code: ApiErrorCode

  constructor(message: string, status: number, code: ApiErrorCode) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

class ApiService {
  private client: AxiosInstance
  private calculatorResponseCache = new CalculatorResponseCache()
  private calculatorDebounce = createCalculatorDebounce(400)

  private invalidateClientSession(): void {
    // Avoid importing the auth store here: auth.ts already imports this API service.
    // Clearing persisted state plus dispatching an event breaks the api.ts -> auth.ts cycle.
    clearPersistedAuthSession()
    dispatchAuthSessionInvalidated()
  }

  private async refreshCsrfToken(): Promise<void> {
    const response = await this.client.get(
      '/api/v1/auth/csrf-token',
      { skipAuthHandler: true } as AxiosRequestConfig & { skipAuthHandler: boolean }
    )
    const hasCookie = document.cookie.includes('csrf_token=')
    const data = response.data as { csrf_token?: unknown } | undefined

    if (!hasCookie && typeof data?.csrf_token === 'string') {
      setCsrfTokenFallback(data.csrf_token)
    }
  }

  private deriveApiErrorCode(status: number, error: unknown): ApiErrorCode {
    if (status === 401 || status === 403) {
      return 'forbidden'
    }

    if (status === 404) {
      return 'not_found'
    }

    const candidate = error as { code?: unknown; message?: unknown }
    const errorCode = typeof candidate.code === 'string' ? candidate.code : ''
    const errorMessage = typeof candidate.message === 'string' ? candidate.message : ''
    const normalizedMessage = errorMessage.toLowerCase()

    const isNetworkOrTimeout = status === 0
      || errorCode === 'ECONNABORTED'
      || errorCode === 'ERR_NETWORK'
      || normalizedMessage.includes('network')
      || normalizedMessage.includes('timeout')

    if (isNetworkOrTimeout) {
      return 'network'
    }

    return 'server_error'
  }

  private toApiError(message: string, status: number, error: unknown): ApiError {
    const code = this.deriveApiErrorCode(status, error)
    return new ApiError(message, status, code)
  }

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // CSRF: attach double-submit token for unsafe methods
        const method = (config.method || '').toUpperCase()
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          const csrfFromCookie = document.cookie
            .split('; ')
            .find(c => c.startsWith('csrf_token='))
            ?.split('=')[1]

          const csrfToken = csrfFromCookie || getCsrfTokenFallback()
          if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken
          }
        }
        return config
      },
      (error: unknown) => {
        const message = error instanceof Error ? error.message : 'Request setup failed'
        return Promise.reject(this.toApiError(message, 0, error))
      }
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        const candidate = error as {
          response?: { status?: number; data?: unknown }
          config?: AxiosRequestConfig & {
            skipAuthHandler?: boolean
            _retryAttempted?: boolean
            _authRefreshAttempted?: boolean
            url?: string
          }
          message?: string
        }

        const status = typeof candidate.response?.status === 'number' ? candidate.response.status : 0
        const config = candidate.config as (AxiosRequestConfig & {
          skipAuthHandler?: boolean
          _retryAttempted?: boolean
          _authRefreshAttempted?: boolean
          url?: string
        }) | undefined

        // Skip global auth handler if requested
        if (config && config.skipAuthHandler) {
          const message = candidate.message || 'API request failed'
          return Promise.reject(this.toApiError(message, status, error))
        }

        const data = candidate.response?.data as {
          error?: boolean
          message?: unknown
          detail?: unknown
        } | undefined

        // 401 Unauthorized — attempt silent token refresh before invalidating session.
        // Guard: skip for the refresh endpoint itself and if we already retried.
        if (status === 401) {
          const requestUrl = config?.url ?? ''
          const isRefreshEndpoint = requestUrl.includes('/auth/refresh')

          if (!isRefreshEndpoint && config && !config._authRefreshAttempted) {
            try {
              await this.client.post(
                '/api/v1/auth/refresh',
                {},
                { skipAuthHandler: true } as AxiosRequestConfig & { skipAuthHandler: boolean }
              )
              // Refresh succeeded — retry the original request once
              config._authRefreshAttempted = true
              return await this.client.request(config)
            } catch {
              // Refresh also failed — fall through to session invalidation
            }
          }

          this.invalidateClientSession()
          const message = candidate.message || 'Unauthorized'
          return Promise.reject(this.toApiError(message, status, error))
        }

        const isCsrfError = data?.message === 'CSRF token missing or invalid'

        // 403 CSRF - refresh the double-submit token and retry once.
        // Calculator endpoints are public, but most unsafe calculator POSTs still
        // pass through CSRF middleware when no Bearer token is present.
        if (status === 403 && isCsrfError) {
          if (!config) {
            this.invalidateClientSession()
            return Promise.reject(this.toApiError('Forbidden', status, error))
          }

          const extendedConfig = config as AxiosRequestConfig & { _retryAttempted?: boolean }

          if (extendedConfig._retryAttempted) {
            this.invalidateClientSession()
            return Promise.reject(this.toApiError('Forbidden', status, error))
          }

          extendedConfig._retryAttempted = true
          try {
            await this.refreshCsrfToken()
            return await this.client.request(extendedConfig)
          } catch (retryError: unknown) {
            this.invalidateClientSession()
            const retryMessage = retryError instanceof Error ? retryError.message : 'Forbidden'
            return Promise.reject(this.toApiError(retryMessage, status, retryError))
          }
        }

        // Extract server-provided error message (custom API error format)
        let serverMessage: string | undefined

        if (data && typeof data === 'object') {
          // Handle custom error format {error: true, category, message}
          if (data.error === true && data.message) {
            serverMessage = String(data.message)
          }
          // Handle validation errors (Pydantic format: {detail: [{loc, msg, type}]})
          else if (Array.isArray(data.detail)) {
            serverMessage = data.detail.map((err) => {
              const typedError = err as { loc?: string[]; msg?: string; type?: string }
              return `${typedError.loc?.join('.')}: ${typedError.msg}`
            }).join('; ')
          } else if (Array.isArray(data.message)) {
            // Handle array message format
            serverMessage = data.message.map((msg) =>
              typeof msg === 'object' ? JSON.stringify(msg) : String(msg)
            ).join('; ')
          } else {
            const messageCandidate = data.message
            const detailCandidate = data.detail

            if (typeof messageCandidate === 'string') {
              serverMessage = messageCandidate
            } else if (typeof detailCandidate === 'string') {
              serverMessage = detailCandidate
            } else {
              try {
                serverMessage = JSON.stringify(data)
              } catch {
                serverMessage = undefined
              }
            }
          }
        }

        const fallbackMessage = candidate.message || 'API request failed'
        const message = serverMessage || fallbackMessage

        if (import.meta.env.DEV) {
          console.error('API Error', { status, data, message })
        } else {
          console.error('API Error', { status, data: stripSensitiveLogData(data), message })
        }

        return Promise.reject(this.toApiError(message, status, error))
      }
    )
  }

  // Generic HTTP methods
  async get<T>(endpoint: string, configOrParams?: AxiosRequestConfig | Record<string, unknown>): Promise<AxiosResponse<T>> {
    // Check if it's likely a config object (has params, headers, or our custom skipAuthHandler)
    const isConfig = configOrParams && (
      'params' in configOrParams ||
      'headers' in configOrParams ||
      'skipAuthHandler' in configOrParams ||
      'timeout' in configOrParams ||
      'responseType' in configOrParams
    )

    // If it looks like a config object or is undefined/null, treat as config
    // Otherwise treat as legacy query params for backward compatibility
    if (isConfig || !configOrParams) {
      return this.client.get(endpoint, configOrParams)
    }

    return this.client.get(endpoint, { params: configOrParams })
  }

  async post<T>(
    endpoint: string,
    data?: Record<string, unknown>,
    config?: AxiosRequestConfig & { skipResponseCache?: boolean; skipCalculatorDebounce?: boolean }
  ): Promise<AxiosResponse<T>> {
    const cacheable = shouldCacheCalculatorRequest(endpoint) && !config?.skipResponseCache

    if (cacheable) {
      const runRequest = async (): Promise<AxiosResponse<T>> => {
        const cacheKey = buildCalculatorCacheKey(endpoint, data)
        const cached = this.calculatorResponseCache.get(cacheKey)
        if (cached) {
          return cached as AxiosResponse<T>
        }

        const response = await this.client.post<T>(endpoint, data, config)
        this.calculatorResponseCache.set(cacheKey, response)
        return response
      }

      if (config?.skipCalculatorDebounce) {
        return runRequest()
      }

      return this.calculatorDebounce.debounced(endpoint, runRequest)
    }

    return this.client.post(endpoint, data, config)
  }

  async put<T>(endpoint: string, data?: Record<string, unknown>): Promise<AxiosResponse<T>> {
    return this.client.put(endpoint, data)
  }

  async delete<T>(endpoint: string): Promise<AxiosResponse<T>> {
    return this.client.delete(endpoint)
  }

  async patch<T>(endpoint: string, data?: Record<string, unknown>): Promise<AxiosResponse<T>> {
    return this.client.patch(endpoint, data)
  }
}

export const apiService = new ApiService()
export const apiClient = apiService
export default apiService
